// Manage storage (like WhatsApp's): how much this device holds, the caches
// you can clear (files download again when opened), and the biggest files —
// over 5 MB, and chat by chat — to review and delete for yourself.
import { useEffect, useState } from "preact/hooks";
import type { Msg } from "../db";
import { formatSize } from "../media";
import * as S from "../state";
import { listTime } from "../util";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";
import { Confirm, Drawer } from "./Panels";
import { useMediaSrc } from "./mediaSrc";

const CACHES: [string, string, string][] = [
  ["xmm-media", "Photos, videos and files", "They download again when you open them."],
  ["xmm-tiles", "Map tiles", "For location messages."],
  ["transformers-cache", "Speech model", "For voice message transcripts; downloads again when needed."],
];
const LARGE = 5 * 1024 * 1024;

async function cacheSize(name: string): Promise<number> {
  if (typeof caches === "undefined" || !(await caches.has(name))) return 0;
  const c = await caches.open(name);
  let total = 0;
  for (const req of await c.keys()) {
    const res = await c.match(req);
    const len = Number(res?.headers.get("content-length") || 0);
    total += len || (res ? (await res.blob()).size : 0);
  }
  return total;
}

/** Forget cached copies of these files (they're deleted for me). */
async function evict(urls: string[]) {
  if (typeof caches === "undefined" || !(await caches.has("xmm-media"))) return;
  const c = await caches.open("xmm-media");
  await Promise.all(urls.map((u) => c.delete(new URL(u, location.href).href)));
}

const size = (m: Msg) => m.media?.size ?? 0;

export function StoragePanel() {
  const [est, setEst] = useState<{ usage: number; quota: number; idb?: number; caches?: number } | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [sizes, setSizes] = useState<Record<string, number>>({});
  const [items, setItems] = useState<Msg[] | null>(null);
  const [review, setReview] = useState<{ title: string; filter: (m: Msg) => boolean } | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);

  async function load() {
    const e = (await navigator.storage?.estimate?.().catch(() => null)) as (StorageEstimate & { usageDetails?: Record<string, number> }) | null;
    if (e) setEst({ usage: e.usage ?? 0, quota: e.quota ?? 0, idb: e.usageDetails?.indexedDB, caches: e.usageDetails?.caches });
    setPersisted((await navigator.storage?.persisted?.().catch(() => null)) ?? null);
    const s: Record<string, number> = {};
    for (const [name] of CACHES) s[name] = await cacheSize(name).catch(() => 0);
    setSizes(s);
    setItems(await engine.value!.mediaMessages());
  }
  useEffect(() => void load(), []);

  async function clear(name: string) {
    await caches.delete(name);
    toast("Cleared");
    void load();
  }

  if (review && items)
    return (
      <Review
        title={review.title}
        items={items.filter(review.filter)}
        onBack={() => setReview(null)}
        onDeleted={(gone) => setItems(items.filter((m) => !gone.has(m.key)))}
      />
    );

  const large = items?.filter((m) => size(m) > LARGE) ?? [];
  const byChat = new Map<string, { n: number; bytes: number }>();
  for (const m of items ?? []) {
    const c = byChat.get(m.chat) ?? { n: 0, bytes: 0 };
    byChat.set(m.chat, { n: c.n + 1, bytes: c.bytes + size(m) });
  }
  const chats = [...byChat].sort((a, b) => b[1].bytes - a[1].bytes);
  const pct = (n = 0) => (est?.usage ? `${Math.max(1, Math.round((n / est.usage) * 100))}%` : "0%");

  return (
    <Drawer title="Storage">
      <section class="storage-summary" aria-label="Storage used">
        {est ? (
          <>
            <div class="storage-figures">
              <strong>{formatSize(est.usage)}</strong> used
              <span class="muted"> · {formatSize(Math.max(0, est.quota - est.usage))} free for xmm</span>
            </div>
            <div class="storage-bar" role="img" aria-label={`${formatSize(est.usage)} of ${formatSize(est.quota)}`}>
              {est.idb !== undefined && <span class="seg chats" style={{ width: pct(est.idb) }} title="Chats" />}
              {est.caches !== undefined && <span class="seg cache" style={{ width: pct(est.caches) }} title="App & cached files" />}
            </div>
            {est.idb !== undefined && (
              <div class="storage-legend small">
                <span>
                  <i class="dot chats" /> Chats {formatSize(est.idb)}
                </span>
                <span>
                  <i class="dot cache" /> App & cached files {formatSize(est.caches ?? 0)}
                </span>
              </div>
            )}
          </>
        ) : (
          <p class="muted">Working out how much is used…</p>
        )}
        {persisted === false && (
          <p class="small storage-persist">
            Your browser may clear xmm's data when space runs low.{" "}
            <button
              class="link"
              onClick={() =>
                void navigator.storage.persist().then((ok) => {
                  setPersisted(ok);
                  toast(ok ? "Your chats will be kept" : "The browser said no — installing the app usually helps");
                })
              }
            >
              Keep my data
            </button>
          </p>
        )}
        {persisted && <p class="small muted">Stored persistently: the browser won't clear it on its own.</p>}
      </section>

      <h3 class="section">Cached on this device</h3>
      <ul class="storage-list">
        {CACHES.map(([name, label, hint]) => (
          <li key={name}>
            <span class="grow">
              <span>{label}</span>
              <small class="muted">{hint}</small>
            </span>
            <span class="muted small">{sizes[name] === undefined ? "…" : formatSize(sizes[name])}</span>
            <button class="chip" disabled={!sizes[name]} onClick={() => setConfirm(name)} aria-label={`Clear ${label}`}>
              Clear
            </button>
          </li>
        ))}
      </ul>

      <h3 class="section">Review and delete items</h3>
      {!items ? (
        <p class="muted pad">Looking through your chats…</p>
      ) : (
        <ul class="storage-list chats">
          <li class="clickable" tabIndex={0} onClick={() => setReview({ title: "Larger than 5 MB", filter: (m) => size(m) > LARGE })} aria-label="Larger than 5 MB">
            <span class="list-action-icon">
              <Icon name="document" />
            </span>
            <span class="grow">Larger than 5 MB</span>
            <span class="muted small">
              {large.length} · {formatSize(large.reduce((t, m) => t + size(m), 0))}
            </span>
          </li>
          {chats.map(([jid, c]) => (
            <li key={jid} class="clickable storage-chat" tabIndex={0} data-jid={jid} onClick={() => setReview({ title: S.displayName(jid), filter: (m) => m.chat === jid })}>
              <Avatar url={S.isGroup(jid) ? S.groups.value.get(jid)?.avatar_url : S.profiles.value.get(jid)?.avatar_url} jid={jid} name={S.displayName(jid)} size={36} />
              <span class="grow">{S.displayName(jid)}</span>
              <span class="muted small">
                {c.n} item{c.n === 1 ? "" : "s"} · {formatSize(c.bytes)}
              </span>
            </li>
          ))}
          {!chats.length && <li class="empty">No photos, videos or files yet.</li>}
        </ul>
      )}
      {confirm && (
        <Confirm
          text={`Clear ${CACHES.find((c) => c[0] === confirm)![1].toLowerCase()} from this device?`}
          action="Clear"
          onCancel={() => setConfirm(null)}
          onConfirm={() => (void clear(confirm), setConfirm(null))}
        />
      )}
    </Drawer>
  );
}

function Review({ title, items, onBack, onDeleted }: { title: string; items: Msg[]; onBack: () => void; onDeleted: (gone: Set<string>) => void }) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const list = [...items].sort((a, b) => size(b) - size(a));
  const chosen = list.filter((m) => picked.has(m.key));
  const total = chosen.reduce((t, m) => t + size(m), 0);
  const toggle = (k: string) => setPicked((p) => (p.has(k) ? new Set([...p].filter((x) => x !== k)) : new Set([...p, k])));
  async function remove() {
    setBusy(true);
    const e = engine.value!;
    for (const m of chosen) await e.hide(m);
    await evict(chosen.map((m) => m.media!.url!)).catch(() => {});
    toast(`Deleted ${chosen.length} item${chosen.length === 1 ? "" : "s"} · ${formatSize(total)} freed`);
    onDeleted(new Set(chosen.map((m) => m.key)));
    setPicked(new Set());
    setBusy(false);
  }
  return (
    <div class="drawer storage-review" role="dialog" aria-label={title}>
      <header class="drawer-head">
        <button class="icon-btn" onClick={onBack} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2 class="grow">{title}</h2>
        {list.length > 0 && (
          <button class="link" onClick={() => setPicked(picked.size === list.length ? new Set() : new Set(list.map((m) => m.key)))}>
            {picked.size === list.length ? "Select none" : "Select all"}
          </button>
        )}
      </header>
      <div class="drawer-body">
        <ul class="storage-items">
          {list.map((m) => (
            <li key={m.key} class={picked.has(m.key) ? "on" : ""} onClick={() => toggle(m.key)} data-key={m.key}>
              <span class={"check" + (picked.has(m.key) ? " on" : "")}>{picked.has(m.key) && <Icon name="check" size={16} />}</span>
              <Thumb m={m} />
              <span class="grow">
                <span class="ellipsis">{m.media?.name || (m.kind === "image" ? "Photo" : m.kind === "video" ? (m.media?.gif ? "GIF" : "Video") : m.kind === "voice" ? "Voice message" : "File")}</span>
                <small class="muted">
                  {S.displayName(m.chat)} · {listTime(m.ts)}
                </small>
              </span>
              <span class="muted small">{formatSize(size(m))}</span>
            </li>
          ))}
          {!list.length && <li class="empty">Nothing here.</li>}
        </ul>
      </div>
      {chosen.length > 0 && (
        <footer class="storage-foot">
          <button class="primary danger-btn" disabled={busy} onClick={() => setConfirm(true)}>
            Delete {chosen.length} item{chosen.length === 1 ? "" : "s"} ({formatSize(total)})
          </button>
        </footer>
      )}
      {confirm && (
        <Confirm
          text={`Delete ${chosen.length} item${chosen.length === 1 ? "" : "s"} for you? Others in the chat still have them.`}
          action="Delete"
          onCancel={() => setConfirm(false)}
          onConfirm={() => (setConfirm(false), void remove())}
        />
      )}
    </div>
  );
}

function Thumb({ m }: { m: Msg }) {
  const visual = m.kind === "image" || (m.kind === "video" && !!m.media?.thumb);
  const { src } = useMediaSrc(m.kind === "image" ? m.media : undefined, m.kind === "image" && !m.media?.thumb);
  const url = m.media?.thumb || src;
  return visual && url ? (
    <img class="storage-thumb" src={url} alt="" />
  ) : (
    <span class="storage-thumb icon">
      <Icon name={m.kind === "voice" || m.kind === "audio" ? "mic" : m.kind === "video" ? "videocam" : "document"} size={20} />
    </span>
  );
}
